// ============= PER-STORE DASHBOARD DATA =============
// Same wire contract as useDashboardData (one POST to `dashboard-data` → one
// complete filled DashboardSpec, no normalizePartial firewall), scoped to a
// single store via { clientId, storeNumber }. HTTP failures carry the status
// so the store view can tell "store not found" (404) apart from the generic
// failure line.

import { useQuery } from "@tanstack/react-query";
import { getFirebaseIdToken } from "@/shared/integrations/firebase/client";
import { captureEdgeFailure } from "@/shared/integrations/sentry/reportEdgeFailure";
import {
  SUPABASE_URL,
  SUPABASE_PUBLISHABLE_KEY,
} from "@/shared/integrations/supabase/client";
import type { DashboardSpec } from "../components/types";

const ENDPOINT = "dashboard-data";

/** Request failure carrying the HTTP status — the store view branches on 404. */
export class DashboardDataRequestError extends Error {
  status: number;

  constructor(status: number) {
    super(`Dashboard data request failed (HTTP ${status})`);
    this.name = "DashboardDataRequestError";
    this.status = status;
  }
}

export function useStoreDashboardData(
  clientId: string | null,
  storeNumber: string | null,
) {
  return useQuery({
    queryKey: ["dashboard-data", clientId, "store", storeNumber],
    enabled: !!clientId && !!storeNumber,
    staleTime: 5 * 60 * 1000,
    retry: 1,
    queryFn: async (): Promise<DashboardSpec> => {
      const idToken = await getFirebaseIdToken();
      if (!idToken) throw new Error("Please sign in to view the dashboard.");

      const resp = await fetch(`${SUPABASE_URL}/functions/v1/${ENDPOINT}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_PUBLISHABLE_KEY,
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ clientId, storeNumber }),
      });

      if (!resp.ok) {
        captureEdgeFailure({ endpoint: ENDPOINT, status: resp.status });
        throw new DashboardDataRequestError(resp.status);
      }

      const spec = (await resp.json()) as DashboardSpec;
      if (!Array.isArray(spec?.sections)) {
        throw new Error("Malformed dashboard response");
      }
      return spec;
    },
  });
}
